import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Build a compact page-number list: always show first + last + a window of
 * pages around the current one. Returns numbers (clickable) and "…" tokens
 * (ellipsis placeholders, not clickable). Caps the window so very long lists
 * stay one row.
 */
const buildPageItems = (current, totalPages) => {
  if (!totalPages || totalPages <= 1) return [1];
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const items = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages - 1, current + 1);
  if (start > 2) items.push("…");
  for (let i = start; i <= end; i++) items.push(i);
  if (end < totalPages - 1) items.push("…");
  items.push(totalPages);
  return items;
};

/**
 * Reusable PaginationControls component
 * Displays pagination information and controls for backend-driven pagination
 * 
 * @param {Object} pagination - Pagination metadata from usePagination hook
 * @param {Function} onPageChange - Callback when page changes (receives new page number)
 * @param {Function} onPageSizeChange - Callback when page size changes (receives new size)
 * @param {string} itemName - Name of the items being paginated (e.g., "properties", "customers") - defaults to "items"
 * @param {boolean} showInfo - Whether to show "Showing X to Y of Z items" info
 * @param {string} variant - Layout variant: "default" (full) or "compact" (inline table row) - defaults to "default"
 * @param {string} className - Additional CSS classes for the container
 */
const PaginationControls = ({ 
  pagination, 
  onPageChange, 
  onPageSizeChange, 
  itemName = 'items',
  showInfo = true,
  variant = 'default',
  className = ''
}) => {
  const { page, pageSize, total, totalPages, hasNext, hasPrevious } = pagination;

  // Calculate display range
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  // Compact variant for inline table row
  if (variant === 'compact') {
    return (
      <div className={`w-full bg-gray-50 border-t border-gray-200 ${className}`}>
        <div className="flex justify-between items-center px-4 py-2 text-xs">
          {showInfo && (
            <span className="text-gray-600">
              Showing {start}-{end} of {total}
            </span>
          )}
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={!hasPrevious}
              className="px-2 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white transition-colors text-xs"
              aria-label="Previous page"
            >
              ◀
            </button>
            
            <span className="text-xs px-3 font-medium flex items-center justify-center">
              <span>{page}</span>
              <span className="mx-1">/</span>
              <span>{totalPages || 1}</span>
            </span>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={!hasNext}
              className="px-2 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white transition-colors text-xs"
              aria-label="Next page"
            >
              ▶
            </button>
            
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="px-2 py-1 text-xs border rounded bg-white hover:bg-gray-50 cursor-pointer focus:outline-none"
              aria-label="Items per page"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>
    );
  }

  // List/Grid variant — bare row of text + buttons + page-size select.
  // No card chrome; the consumer page is responsible for positioning
  // (typically pinned to the bottom of the page area via flex layout).
  if (variant === 'list' || variant === 'grid') {
    const pageItems = buildPageItems(page, totalPages || 1);
    return (
      <div className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 ${className}`}>
        {showInfo && (
          <div className="text-sm text-slate-600">
            Showing{" "}
            <span className="font-semibold text-slate-900 tabular-nums">{start}</span>
            {start !== end && (
              <>
                {" – "}
                <span className="font-semibold text-slate-900 tabular-nums">{end}</span>
              </>
            )}{" "}
            of{" "}
            <span className="font-semibold text-flamingo-600 tabular-nums">{total}</span>{" "}
            {itemName}
          </div>
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <div className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={() => onPageChange(page - 1)}
              disabled={!hasPrevious}
              className="w-8 h-8 rounded-md flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft size={16} />
            </button>

            {pageItems.map((p, i) =>
              p === "…" ? (
                <span
                  key={`ellipsis-${i}`}
                  className="w-8 h-8 flex items-center justify-center text-slate-400 text-sm select-none"
                >
                  …
                </span>
              ) : (
                <button
                  type="button"
                  key={p}
                  onClick={() => onPageChange(p)}
                  className={`min-w-[2rem] h-8 px-2 rounded-md text-sm font-medium tabular-nums transition-colors ${
                    p === page
                      ? "bg-gradient-to-r from-flamingo-500 to-flamingo-600 text-white shadow-sm"
                      : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                  aria-current={p === page ? "page" : undefined}
                  aria-label={`Go to page ${p}`}
                >
                  {p}
                </button>
              )
            )}

            <button
              type="button"
              onClick={() => onPageChange(page + 1)}
              disabled={!hasNext}
              className="w-8 h-8 rounded-md flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
              aria-label="Next page"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="px-2 py-1 text-sm border border-slate-300 rounded-md bg-white hover:border-slate-400 cursor-pointer focus:outline-none focus:border-flamingo-500 focus:ring-1 focus:ring-flamingo-200 text-slate-700 h-8"
            aria-label="Items per page"
          >
            <option value={12}>12 / page</option>
            <option value={24}>24 / page</option>
            <option value={36}>36 / page</option>
            <option value={48}>48 / page</option>
            <option value={60}>60 / page</option>
          </select>
        </div>
      </div>
    );
  }

  // Default full-width variant
  return (
    <div className={`flex justify-between items-center mt-4 p-4 border-t border-gray-200 ${className}`}>
      {showInfo && (
        <div className="text-sm text-gray-600">
          Showing {start} to {end} of {total} {itemName}
        </div>
      )}
      
      <div className="flex items-center gap-4">
        {/* Previous Button */}
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPrevious}
          className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
          aria-label="Previous page"
        >
          ◀ Previous
        </button>

        {/* Page Numbers */}
        <span className="text-sm px-4 font-medium">
          Page {page} of {totalPages || 1}
        </span>

        {/* Next Button */}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNext}
          className="px-4 py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
          aria-label="Next page"
        >
          Next ▶
        </button>

        {/* Page Size Selector */}
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="px-3 py-2 border rounded bg-white hover:bg-gray-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Items per page"
        >
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={30}>30 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
        </select>
      </div>
    </div>
  );
};

export default PaginationControls;

